import { supabase } from '../configs/supabase';

// Note: We no longer need to load Stripe.js since we're redirecting directly to checkout URLs
// The Stripe.js library is only needed if you want to use Stripe Elements for custom forms

/**
 * Create a Stripe Checkout session for subscription
 * @param {string} priceId - Stripe price ID for the plan
 * @param {string} userId - User ID from Supabase auth
 * @returns {Promise<{url: string, sessionId: string, error: object}>}
 */
export const createCheckoutSession = async (priceId, userId) => {
  try {
    // Call Supabase Edge Function to create checkout session
    // The Edge Function will handle the Stripe API call with secret key
    const { data, error } = await supabase.functions.invoke('stripe-checkout', {
      body: {
        priceId,
        userId,
        mode: 'subscription',
      },
    });

    if (error) {
      console.error('Error creating checkout session:', error);
      return { url: null, sessionId: null, error };
    }

    return { url: data.url, sessionId: data.sessionId, error: null };
  } catch (error) {
    console.error('Exception creating checkout session:', error);
    return { url: null, sessionId: null, error };
  }
};

/**
 * Redirect to Stripe Checkout
 * @param {string} priceId - Stripe price ID
 * @param {string} userId - User ID
 */
export const redirectToCheckout = async (priceId, userId) => {
  try {
    const { url, error } = await createCheckoutSession(priceId, userId);

    if (error || !url) {
      throw new Error(error?.message || 'Failed to create checkout session');
    }

    // Redirect directly to the checkout URL
    window.location.href = url;
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

/**
 * Create a Stripe Customer Portal session
 * @param {string} customerId - Stripe customer ID
 * @returns {Promise<{url: string, error: object}>}
 */
export const createPortalSession = async (customerId) => {
  try {
    const { data, error } = await supabase.functions.invoke('stripe-portal', {
      body: {
        customerId,
      },
    });

    if (error) {
      console.error('Error creating portal session:', error);
      return { url: null, error };
    }

    return { url: data.url, error: null };
  } catch (error) {
    console.error('Exception creating portal session:', error);
    return { url: null, error };
  }
};

/**
 * Redirect to Stripe Customer Portal
 * @param {string} customerId - Stripe customer ID
 */
export const redirectToPortal = async (customerId) => {
  try {
    const { url, error } = await createPortalSession(customerId);

    if (error || !url) {
      throw new Error(error?.message || 'Failed to create portal session');
    }

    window.location.href = url;
  } catch (error) {
    console.error('Error redirecting to portal:', error);
    throw error;
  }
};
